import type { InvestorCase } from '@fruuit/shared'
import {
  Avatar,
  Badge,
  Button,
  buttonClasses,
  Callout,
  Card,
  EmptyState,
  PageHeader,
  SkeletonText,
} from '@fruuit/ui'
import { FileSearch, MessageSquarePlus, UsersRound } from 'lucide-react'
import { useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router'
import { ClientActionsPanel } from '../../features/client-actions/ClientActionsPanel'
import {
  CaseSummary,
  EngagementRecord,
  MeetingsCard,
  ObjectiveCard,
} from '../../features/engagements/CaseParts'
import { CaseHistory, ResponsivenessCard } from '../../features/engagements/CaseRecord'
import { plural, shortDate, themeLabel } from '../../features/engagements/format'
import { useInvestorCase } from '../../features/engagements/queries'
import { ParticipationCard } from '../../features/participation/ParticipationCard'
import { RequestParticipationDialog } from '../../features/participation/RequestParticipationDialog'
import { AskManagerDialog } from '../../features/questions/AskManagerDialog'
import { QuestionThread } from '../../features/questions/QuestionThread'
import { useTitle } from '../../lib/useTitle'

const meetingPath = (id: string) => `/app/meetings/${id}`

/** S5 Case (investor): released objectives, the engagement record, and your requests. */
export function CasePage() {
  const { id = '' } = useParams()
  const query = useInvestorCase(id)
  useTitle(query.data?.issuer.name, 'Engagement')

  if (query.isPending) {
    return (
      <div aria-busy="true" className="space-y-6">
        <SkeletonText lines={2} className="max-w-xl" />
        <SkeletonText lines={8} />
      </div>
    )
  }
  if (query.isError) {
    return query.error.code === 'not_found' ? (
      <EmptyState
        icon={<FileSearch />}
        title="This engagement is not available."
        description="It may not be released yet, or your organisation is not subscribed to it."
        action={
          <Link to="/app/engagements" className={buttonClasses({ variant: 'secondary' })}>
            Back to engagements
          </Link>
        }
      />
    ) : (
      <Callout tone="danger" role="alert">
        {query.error.message}
      </Callout>
    )
  }
  return <CaseView data={query.data} />
}

function CaseView({ data }: { data: InvestorCase }) {
  const [asking, setAsking] = useState(false)
  const [params, setParams] = useSearchParams()
  // The meeting page's "Request to participate" links here with `?request=<meeting id>`.
  const requestedMeeting =
    data.meetings.items.find((m) => m.id === params.get('request') && m.status === 'published') ??
    null
  const [requesting, setRequesting] = useState(requestedMeeting !== null)
  const [meeting, setMeeting] = useState(requestedMeeting)
  const eligibility = data.participation.eligibility

  function closeRequest() {
    setRequesting(false)
    setMeeting(null)
    if (params.has('request')) {
      params.delete('request')
      setParams(params, { replace: true })
    }
  }

  return (
    <>
      <PageHeader
        breadcrumbs={[
          { label: 'Engagement programme', href: '/app/engagements' },
          { label: data.issuer.name },
        ]}
        eyebrow={data.theme ? themeLabel(data.theme) : 'Company engagement'}
        title={data.issuer.name}
        description={data.title}
        actions={
          <>
            <Link
              to={`/app/companies/${data.issuer.id}`}
              className={buttonClasses({ variant: 'secondary' })}
            >
              Company research
            </Link>
            <Button
              onClick={() => setRequesting(true)}
              disabled={!eligibility.can_request}
              aria-describedby={eligibility.can_request ? undefined : 'participation-ineligible'}
            >
              Request participation
            </Button>
          </>
        }
      />
      {!eligibility.can_request && eligibility.message ? (
        <Callout tone="note" className="-mt-4 mb-6" role="status">
          <span id="participation-ineligible">{eligibility.message}</span>
          {eligibility.reason === 'engagement_closed' ? (
            <>
              {' '}
              <button
                type="button"
                className="font-semibold underline underline-offset-4"
                onClick={() => setAsking(true)}
              >
                Ask the manager
              </button>
            </>
          ) : null}
        </Callout>
      ) : null}

      <CaseSummary
        status={data.status}
        nextAction={data.next_action}
        extra={data.client_specific ? <Badge tone="outline">Your organisation only</Badge> : null}
        facts={[
          { label: 'Manager', value: data.manager_name },
          { label: 'Next deadline', value: shortDate(data.due_date) },
          { label: 'Engagement started', value: shortDate(data.original_start) },
          { label: 'Fruuit coverage since', value: shortDate(data.fruuit_start) },
        ]}
      />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="min-w-0 space-y-6">
          <Card
            eyebrow="The change we seek"
            title="Objectives & reviewed progress"
            actions={
              <span className="text-xs text-sage">
                {plural(data.objectives.length, 'objective')} · {data.progress.percent}% progress
              </span>
            }
          >
            {data.objectives.length === 0 ? (
              <EmptyState
                variant="plain"
                title="No objectives have been released yet."
                className="py-8"
              />
            ) : (
              data.objectives.map((objective) => (
                <ObjectiveCard key={objective.id} objective={objective} caseTheme={data.theme} />
              ))
            )}
          </Card>
          <EngagementRecord
            dialogues={data.dialogues}
            meetings={data.meetings}
            meetingPath={meetingPath}
          />
          <CaseHistory items={data.history} />
          <Card>
            <ClientActionsPanel
              companies={[
                { issuerId: data.issuer.id, name: data.issuer.name, engagementId: data.id },
              ]}
              engagementId={data.id}
            />
          </Card>
        </div>

        <aside className="min-w-0 space-y-6" aria-label="Case contacts and requests">
          <Card variant="tint" eyebrow="Your relationship team" padding="md">
            <div className="flex items-center gap-3">
              <Avatar name={data.manager_name} size="lg" />
              <div>
                <p className="font-semibold text-ink">{data.manager_name}</p>
                <p className="text-xs text-sage">Engagement manager</p>
              </div>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-ink-soft">
              Questions about the evidence, company response or next step stay with this case.
            </p>
            <Button
              className="mt-4"
              fullWidth
              variant="secondary"
              icon={<MessageSquarePlus />}
              onClick={() => setAsking(true)}
            >
              Ask the manager
            </Button>
            <p className="mt-4 border-t border-line pt-3 text-xs text-sage">
              Independent research reviewer:{' '}
              <span className="font-medium text-ink-soft">{data.reviewer_name}</span>
            </p>
          </Card>

          <ResponsivenessCard value={data.responsiveness} />
          <MeetingsCard meetings={data.meetings} meetingPath={meetingPath} />

          <Card
            title="Your participation"
            actions={
              <span className="text-xs text-sage">{data.participation.requests.length}</span>
            }
          >
            {data.participation.requests.length === 0 ? (
              <EmptyState
                variant="plain"
                icon={<UsersRound />}
                title="No participation requests yet."
                description={
                  eligibility.can_request
                    ? 'Ask to discuss the case, join a company meeting or receive the next evidence update.'
                    : (eligibility.message ?? undefined)
                }
                className="py-6"
              />
            ) : (
              <div className="space-y-4">
                {data.participation.requests.map((r) => (
                  <ParticipationCard key={r.id} variant="investor" request={r} />
                ))}
              </div>
            )}
          </Card>

          <Card
            title="Your questions"
            actions={<span className="text-xs text-sage">{data.questions.length}</span>}
          >
            {data.questions.length === 0 ? (
              <p className="text-sm text-sage">
                No questions have been raised for this engagement.
              </p>
            ) : (
              <div className="space-y-4">
                {data.questions.map((q) => (
                  <QuestionThread key={q.id} variant="investor" question={q} />
                ))}
              </div>
            )}
          </Card>
        </aside>
      </div>

      <AskManagerDialog
        open={asking}
        onClose={() => setAsking(false)}
        engagementId={data.id}
        managerName={data.manager_name}
        companyName={data.issuer.name}
      />
      {requesting ? (
        <RequestParticipationDialog
          open
          onClose={closeRequest}
          engagementId={data.id}
          companyName={data.issuer.name}
          eligibility={eligibility}
          meeting={meeting}
        />
      ) : null}
    </>
  )
}
